import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import { Box, Dialog, Divider, IconButton, Typography } from "@mui/material";

function getJoinedYear(companyDetail) {
  const joinedYear =
    companyDetail?.joinedYear ||
    companyDetail?.joined_year ||
    companyDetail?.year;

  if (joinedYear) return joinedYear;

  const date =
    companyDetail?.createdAt ||
    companyDetail?.created_at ||
    companyDetail?.joinedAt ||
    companyDetail?.joined_at;

  if (!date) return null;

  const yearMatch = date.toString().match(/\b\d{4}\b/);

  return yearMatch ? yearMatch[0] : null;
}

function getSsmNumber(companyDetail) {
  return (
    companyDetail?.ssmNumber ||
    companyDetail?.ssm_number ||
    companyDetail?.registrationNumber ||
    companyDetail?.registration_number ||
    "{{ssm_number}}"
  );
}

function VerificationItem({ title, children }) {
  return (
    <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
      <GppGoodOutlinedIcon
        sx={{
          width: "22px",
          height: "22px",
          color: "rgba(0, 0, 0, 0.87)",
          mt: "1px",
          flexShrink: 0,
        }}
      />

      <Box>
        <Typography
          sx={{
            fontFamily: "Inter",
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "24px",
            color: "rgba(0, 0, 0, 0.87)",
          }}
        >
          {title}
        </Typography>

        <Typography
          component="div"
          sx={{
            fontFamily: "Inter",
            fontSize: "15px",
            fontWeight: 400,
            lineHeight: "22px",
            color: "rgba(0, 0, 0, 0.87)",
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

function VerifiedCompanyDialog({
  open,
  onClose,
  companyDetail,
  onMoreAboutCompany,
}) {
  const companyName = companyDetail?.name || "{{company_name}}";
  const ssmNumber = getSsmNumber(companyDetail);
  const joinedYear = getJoinedYear(companyDetail);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "740px",
          maxWidth: "calc(100vw - 32px)",
          borderRadius: "16px",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.22)",
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.22)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box
        sx={{
          height: "60px",
          px: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Inter",
            fontSize: "21px",
            fontWeight: 700,
            lineHeight: "28px",
            color: "rgba(0, 0, 0, 0.87)",
          }}
        >
          Verified Company
        </Typography>

        <IconButton
          aria-label="Close verified company dialog"
          onClick={onClose}
          size="small"
          sx={{ color: "rgba(0, 0, 0, 0.87)" }}
        >
          <CloseIcon sx={{ width: "24px", height: "24px" }} />
        </IconButton>
      </Box>

      <Box sx={{ px: "24px", pt: "22px", pb: "20px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontSize: "26px",
              fontWeight: 700,
              lineHeight: "34px",
              color: "rgba(0, 0, 0, 0.87)",
            }}
          >
            {companyName}
          </Typography>

          <VerifiedIcon
            aria-label="Verified company"
            sx={{
              width: "32px",
              height: "32px",
              color: "#1EA1F3",
              flexShrink: 0,
            }}
          />
        </Box>

        <Typography
          sx={{
            mt: "10px",
            mb: "22px",
            fontFamily: "Inter",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px",
            color: "rgba(0, 0, 0, 0.87)",
          }}
        >
          This employer has passed Hiredly's comprehensive multi-point
          authenticity screening.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <VerificationItem title="Corporate Entity Match">
            <Box component="ul" sx={{ m: 0, pl: "20px" }}>
              <li>New Business Registration Number: {ssmNumber}</li>
              <li>Director identity & active status officially verified.</li>
            </Box>
          </VerificationItem>

          <VerificationItem title="Digital Security Cleared">
            <Box component="ul" sx={{ m: 0, pl: "20px" }}>
              <li>
                Corporate website and official email domain authenticated.
              </li>
            </Box>
          </VerificationItem>

          <VerificationItem title="Workplace Audited">
            <Box component="ul" sx={{ m: 0, pl: "20px" }}>
              <li>
                Physical operational site audited by Hiredly's Business
                Verification Team.
              </li>
            </Box>
          </VerificationItem>

        {joinedYear ? ( 
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <CalendarTodayOutlinedIcon
              sx={{
                width: "22px",
                height: "22px",
                color: "rgba(0, 0, 0, 0.87)",
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: 700,
                lineHeight: "24px",
                color: "rgba(0, 0, 0, 0.87)",
              }}
            >
              Joined Hiredly since {joinedYear}
            </Typography>
          </Box>
        ) : null}
        </Box>
      </Box>

      <Divider />

      <Box
        sx={{
          minHeight: "68px",
          px: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onMoreAboutCompany}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "18px",
            border: 0,
            padding: 0,
            background: "transparent",
            cursor: "pointer",
            fontFamily: "Inter",
            fontSize: "15px",
            fontWeight: 400,
            lineHeight: "22px",
            color: "rgba(0, 0, 0, 0.87)",
            textDecoration: "underline",
          }}
        >
          More about this company
          <ArrowForwardIcon sx={{ width: "22px", height: "22px" }} />
        </Box>
      </Box>
    </Dialog>
  );
}

export default VerifiedCompanyDialog;
